/**
 * The child's LOOK in the funnel — the subset of the Character Editor's model
 * the funnel actually needs, plus the photo-analysis → look mapping.
 *
 * The editor's own model carries undo, pose sliders, per-layer HSB pickers and
 * three characters. The funnel only ever renders the CHILD, and only ever from
 * a photo, so it keeps just the selections the .riv binds and reuses the
 * editor's vocabulary (`CHARACTER_SKIN_PALETTE`, `EYE_COLOURS`, `HAIR_COLOURS`,
 * the real hair folders) unchanged — a look built here is the same thing the
 * editor would have built from the same analysis.
 *
 * `lookFromAnalysis` mirrors `applyAnalysis` in CharacterEditorScreen field for
 * field: every value is best-effort and guarded, and an out-of-vocabulary answer
 * is SKIPPED rather than invented, so a bad classification degrades to the
 * default look instead of a broken character.
 */
import { EYE_COLOURS, HAIR_COLOURS } from '@editor/contract';
import { CHARACTER_SKIN_PALETTE } from '@editor/characterPalette';
import {
  HAIR_FRONT,
  HAIR_BACK,
  FRECKLES_IMAGES,
  GLASSES_IMAGES,
} from '@editor/editorAssets';
import type { AnalysisResult } from '@editor/photoAnalysis';

/** What the funnel's Rive stage needs to paint one child. */
export interface FunnelLook {
  /** Index into `CHARACTER_SKIN_PALETTE` (0 = darkest). */
  tone: number;
  /** Index into `EYE_COLOURS` — also picks the pupil PNG by colour name. */
  eye: number;
  /** Index into `HAIR_COLOURS` — picks the shade ramp + eyebrow colour. */
  hair: number;
  /** Hair-front / hair-back folder names, or null for "none" (bald). */
  frontStyle: string | null;
  backStyle: string | null;
  /** Overlay file stems, or null to leave the overlay empty. */
  frecklesStyle: string | null;
  glassesStyle: string | null;
  isGirl: boolean;
}

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));
const eq = (a: unknown, b: string) => String(a ?? '').toLowerCase() === b.toLowerCase();

/**
 * The look shown before (or instead of) an analysis — a mid skin tone with the
 * first hairstyle in each folder, which is what the editor opens on.
 */
export function defaultLook(isGirl = true): FunnelLook {
  return {
    tone: Math.floor(CHARACTER_SKIN_PALETTE.length / 2),
    eye: 1,
    hair: 6,
    frontStyle: HAIR_FRONT.styles[0] ?? null,
    backStyle: HAIR_BACK.styles[0] ?? null,
    // Freckles/glasses are driven by the analysis booleans; start with neither.
    frecklesStyle: null,
    glassesStyle: null,
    isGirl,
  };
}

/**
 * Fold a photo-analysis result onto a look.
 *
 * `base` is the look to fall back to per field — pass the current look so a
 * re-analysis only moves what the model was confident about. The analysis
 * `gender` wins over the funnel's own boy/girl answer, because it is read off
 * the actual photo; pass `base.isGirl` from the answer and it stands whenever
 * the model returns neither value.
 */
export function lookFromAnalysis(r: AnalysisResult, base: FunnelLook = defaultLook()): FunnelLook {
  const next: FunnelLook = { ...base };

  if (r.gender === 'girl' || r.gender === 'boy') next.isGirl = r.gender === 'girl';

  if (typeof r.skinTone === 'number' && Number.isFinite(r.skinTone)) {
    next.tone = clamp(Math.round(r.skinTone) - 1, 0, CHARACTER_SKIN_PALETTE.length - 1);
  }

  const ei = EYE_COLOURS.findIndex((c) => eq(r.eyeColour, c.name));
  if (ei >= 0) next.eye = ei;

  const hi = HAIR_COLOURS.findIndex((c) => eq(r.hairColour, c.name));
  if (hi >= 0) next.hair = hi;

  // "none" from the model means a buzz cut — which in this rig is the FRONT
  // layer "Short" (the close-cropped top); an empty front slot renders a bald
  // child. So "none" lands on "Short" when that folder exists.
  const wanted = eq(r.hairFrontStyle, 'none') ? 'Short' : r.hairFrontStyle;
  const fs = HAIR_FRONT.styles.find((s) => eq(wanted, s));
  if (fs) next.frontStyle = fs;
  const bs = HAIR_BACK.styles.find((s) => eq(r.hairBackStyle, s));
  if (bs) next.backStyle = bs;

  // Overlays are booleans in the analysis but IMAGES in the .riv: true picks the
  // folder's first file (the same default the editor uses), false empties the slot.
  if (typeof r.freckles === 'boolean') {
    next.frecklesStyle = r.freckles ? FRECKLES_IMAGES.stems[0] ?? null : null;
  }
  if (typeof r.glasses === 'boolean') {
    next.glassesStyle = r.glasses ? GLASSES_IMAGES.stems[0] ?? null : null;
  }

  return next;
}

